import { nextTick, ref, type Ref } from "vue";
import { useAsyncAutocompleteItems } from "@/composables/useAsyncAutocompleteItems";
import type { PlacePrediction, GoogleMapsPlacesLibrary, AutocompleteRequest } from "../types/GoogleMapsAutoComplete";
import {
	GeoAddress,
	type UserLocation,
	MANUAL_PLACE_ID_PREFIX,
	getRegionFromBrowserLocale,
	isManualAddress,
	parseAddressComponents
} from "@/helpers/location";

export interface UseGooglePlacePredictionsOptions {
	locationBias?: Ref<UserLocation | null | undefined>;
	onPlaceChanged?: (geoAddress: GeoAddress | null) => void;
	preservePredictionLabel?: boolean;
}

export interface UseGooglePlacePredictions {
	modelValue: Ref<GeoAddress | null | undefined>;
	items: Ref<Partial<GeoAddress>[]>;
	isLoading: Ref<boolean>;
	isGeocodingManual: Ref<boolean>;
	userEditedText: Ref<boolean>;
	searchTerm: Ref<string | undefined>;
	setSearchTermSilent: (value: string) => void;
	onSearchInput: (text: string | null | undefined) => void;
	fetchPlacesBySearchTerm: (searchTerm: string) => Promise<Partial<GeoAddress>[]>;
	handleBlur: () => Promise<void>;
	commitPendingInput: () => Promise<void>;
	selectPrediction: (geoAddress: GeoAddress | null) => Promise<void>;
	createManualAddress: (text?: string) => Promise<void>;
}

export function useGooglePlacePredictions(options: UseGooglePlacePredictionsOptions = {}): UseGooglePlacePredictions {
	const modelValue = ref<GeoAddress | null | undefined>();
	const placePredictionMap = ref<Map<string, PlacePrediction>>(new Map());
	const isGeocodingManual = ref(false);
	const userEditedText = ref(false);
	let editVersion = 0;
	let selectionRequestVersion = 0;
	let predictionRequestVersion = 0;
	let manualGeocodeCount = 0;
	let pendingChange: Promise<void> | null = null;
	let wakePendingWait: (() => void) | null = null;
	let pendingSelectedChange: Promise<void> | null = null;
	let applyResolvedSelection: (() => void) | null = null;
	let selectedModelValue: GeoAddress | null = null;
	let selectionFallbackModelValue: GeoAddress | null | undefined;
	let selectedPlaceFailed = false;

	function setPendingChange(change: Promise<void> | null): void {
		if (pendingChange === change) return;
		pendingChange = change;
		wakePendingWait?.();
		wakePendingWait = null;
	}

	function trackPendingChange(change: Promise<void>, isSelectedChange = false): Promise<void> {
		const trackedChange = change.finally(() => {
			if (pendingChange === trackedChange) {
				setPendingChange(null);
			}
			if (isSelectedChange && pendingSelectedChange === trackedChange) {
				pendingSelectedChange = null;
			}
		});
		setPendingChange(trackedChange);
		if (isSelectedChange) {
			pendingSelectedChange = trackedChange;
		}
		return trackedChange;
	}

	async function geocodeAddress(address: string): Promise<{ lat: number; lng: number } | null> {
		//@ts-expect-error - google is not defined in the global scope
		if (!window.google?.maps) {
			return null;
		}
		try {
			//@ts-expect-error - google is not defined in the global scope
			const geocoder = new google.maps.Geocoder();
			const response = await geocoder.geocode({ address });
			if (response.results && response.results.length > 0) {
				const location = response.results[0].geometry.location;
				return { lat: location.lat(), lng: location.lng() };
			}
		} catch (error) {
			console.warn("Geocoding failed for address:", address, error);
		}
		return null;
	}

	async function createAndApplyManualAddress(address: string, addressEditVersion: number): Promise<void> {
		manualGeocodeCount += 1;
		isGeocodingManual.value = true;
		try {
			const coords = await geocodeAddress(address);
			if (addressEditVersion !== editVersion || searchTerm.value?.trim() !== address) {
				return;
			}
			const geoAddress: GeoAddress = {
				distance: "",
				formattedAddress: address,
				latitude: coords?.lat ?? 0,
				longitude: coords?.lng ?? 0,
				placeId: `${MANUAL_PLACE_ID_PREFIX}${crypto.randomUUID()}`
			};
			items.value = [geoAddress];
			modelValue.value = geoAddress;
			selectionFallbackModelValue = geoAddress;
			selectedPlaceFailed = false;
			options.onPlaceChanged?.(geoAddress);
		} finally {
			manualGeocodeCount -= 1;
			isGeocodingManual.value = manualGeocodeCount > 0;
		}
	}

	function applyManualAddress(address: string): Promise<void> {
		editVersion += 1;
		return trackPendingChange(createAndApplyManualAddress(address, editVersion));
	}

	async function createManualAddress(text?: string): Promise<void> {
		userEditedText.value = false;
		const address = text?.trim() || searchTerm.value?.trim();
		if (!address) return;
		await applyManualAddress(address);
		await nextTick();
	}

	const {
		items,
		isLoading,
		searchTerm,
		setValueSilent: setAutocompleteSearchTermSilent,
		onSearchInput: searchItems
	} = useAsyncAutocompleteItems<Partial<GeoAddress>>({
		fetchItems: async (term: string): Promise<Partial<GeoAddress>[]> => {
			if (!term || term.trim().length < 1) {
				if (modelValue.value && isManualAddress(modelValue.value)) {
					return [modelValue.value];
				}
				return [];
			}
			isLoading.value = true;
			try {
				const results = term ? await fetchPlacesBySearchTerm(term) : [];
				if (modelValue.value && isManualAddress(modelValue.value) && modelValue.value.formattedAddress === term) {
					return [modelValue.value, ...results];
				}
				return results;
			} catch (error) {
				console.error("Error fetching place suggestions:", error);
				return [];
			} finally {
				isLoading.value = false;
			}
		},
		getId: (item) => item.placeId ?? ""
	});

	function setSearchTermSilent(value: string): void {
		setAutocompleteSearchTermSilent(value);
		const currentModelValue = modelValue.value;
		if (
			currentModelValue &&
			typeof currentModelValue.latitude === "number" &&
			typeof currentModelValue.longitude === "number" &&
			currentModelValue.formattedAddress.trim() === value.trim()
		) {
			editVersion += 1;
			selectionRequestVersion += 1;
			setPendingChange(null);
			pendingSelectedChange = null;
			applyResolvedSelection = null;
			const canonicalModelValue = isManualAddress(currentModelValue) ? null : currentModelValue;
			selectedModelValue = canonicalModelValue;
			selectionFallbackModelValue = canonicalModelValue;
			selectedPlaceFailed = false;
		}
	}

	function onSearchInput(text: string | null | undefined) {
		if (text === undefined) return;
		text ??= "";
		if (!text.trim()) {
			setPendingChange(null);
			if (!modelValue.value) {
				editVersion += 1;
				selectionRequestVersion += 1;
				pendingSelectedChange = null;
				applyResolvedSelection = null;
				selectedModelValue = null;
				selectionFallbackModelValue = null;
				selectedPlaceFailed = false;
			}
		}
		searchItems(text);
		const matchingSelectedModelValue = [selectedModelValue, selectionFallbackModelValue].find(
			(address) => address && text.trim() === address.formattedAddress.trim()
		);
		if (matchingSelectedModelValue) {
			const restoringFallback = matchingSelectedModelValue === selectionFallbackModelValue;
			const selectionWasReplaced = modelValue.value?.placeId !== matchingSelectedModelValue.placeId;
			modelValue.value = matchingSelectedModelValue;
			selectedModelValue = matchingSelectedModelValue;
			userEditedText.value = false;
			selectedPlaceFailed = false;
			if (pendingSelectedChange) {
				setPendingChange(pendingSelectedChange);
			}
			if (restoringFallback) {
				selectionRequestVersion += 1;
				setPendingChange(null);
				pendingSelectedChange = null;
				applyResolvedSelection = null;
				if (selectionWasReplaced) options.onPlaceChanged?.(matchingSelectedModelValue);
			} else if (applyResolvedSelection) {
				applyResolvedSelection();
			} else if (selectionWasReplaced) {
				options.onPlaceChanged?.(matchingSelectedModelValue);
			}
			return;
		}
		const modelText = modelValue.value?.formattedAddress || "";
		if (text !== modelText || selectedPlaceFailed) {
			editVersion += 1;
			userEditedText.value = true;
		} else {
			userEditedText.value = false;
			if (pendingSelectedChange) {
				setPendingChange(pendingSelectedChange);
			}
			applyResolvedSelection?.();
		}
	}

	async function handleBlur() {
		if (!userEditedText.value) return;

		const currentText = searchTerm.value?.trim() || "";
		if (!currentText) {
			userEditedText.value = false;
			return;
		}

		const modelText = modelValue.value?.formattedAddress?.trim() || "";
		if (currentText === modelText && !selectedPlaceFailed) {
			userEditedText.value = false;
			return;
		}

		userEditedText.value = false;
		await applyManualAddress(currentText);
	}

	async function commitPendingInput(): Promise<void> {
		while (userEditedText.value || pendingChange) {
			if (userEditedText.value) {
				await handleBlur();
			} else if (pendingChange) {
				const currentChange = pendingChange;
				let wakeCurrentWait!: () => void;
				const pendingChangeUpdated = new Promise<void>((resolve) => {
					wakeCurrentWait = resolve;
				});
				wakePendingWait = wakeCurrentWait;
				await Promise.race([currentChange, pendingChangeUpdated]);
				if (wakePendingWait === wakeCurrentWait) {
					wakePendingWait = null;
				}
			}
		}
	}

	const fetchPlacesBySearchTerm = async (searchTerm: string): Promise<Partial<GeoAddress>[]> => {
		const requestVersion = ++predictionRequestVersion;
		//@ts-expect-error - google is not defined in the global scope
		if (!window.google?.maps) {
			console.warn("Google Maps API not loaded yet");
			return [];
		}
		try {
			//@ts-expect-error - google is not defined in the global scope
			const placesLibrary = (await google.maps.importLibrary("places")) as unknown as GoogleMapsPlacesLibrary;
			const { AutocompleteSuggestion } = placesLibrary;

			if (!AutocompleteSuggestion || typeof AutocompleteSuggestion.fetchAutocompleteSuggestions !== "function") {
				throw new Error(
					"google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions is undefined. Did you add 'places' to libraries when loading Google Maps?"
				);
			}

			// Build the autocomplete request with optional location bias
			const request: AutocompleteRequest = {
				input: searchTerm
			};

			// Add location bias if user location is available
			const locationBias = options.locationBias?.value;
			if (locationBias != null && locationBias.latitude != null && locationBias.longitude != null) {
				request.locationBias = {
					lat: locationBias.latitude,
					lng: locationBias.longitude
				};
			}

			// LSP-15387: Bias by the browser's locale region (e.g. "en-US" → "US") so
			// results favor the user's country. Applied alongside any explicit
			// locationBias above; soft bias only — results from other countries
			// still appear.
			const region = getRegionFromBrowserLocale();
			if (region) {
				request.region = region;
			}

			const result = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
			return result.suggestions.map((s) => {
				const pred = s.placePrediction;
				if (requestVersion === predictionRequestVersion) {
					placePredictionMap.value.set(pred.placeId, pred);
				}
				return {
					placeId: pred.placeId,
					formattedAddress: pred.text ? String(pred.text) : "" // Note this gives a address that does not always match the formatted address found after selecting.
				};
			});
		} catch (error) {
			console.error("Error fetching place suggestions:", error);
			return [];
		}
	};

	/*
	 * This function needs to exist because we need the latitude and longitude when saving the value to API.
	 * The search term google API doesn't return the latitude and longitude, so we need to fetch it when they select a value.
	 */
	async function resolveSelectedPlace(
		geoAddress: GeoAddress,
		selectionEditVersion: number,
		selectedRequestVersion: number
	): Promise<void> {
		let resolvedPlaceId: string | undefined;
		let resolvedFormattedAddress: string | undefined;
		const isCurrentSelection = () =>
			selectedRequestVersion === selectionRequestVersion &&
			(selectionEditVersion === editVersion ||
				(!userEditedText.value &&
					[geoAddress.formattedAddress, resolvedFormattedAddress].some(
						(address) => address && searchTerm.value?.trim() === address.trim()
					))) &&
			[geoAddress.placeId, resolvedPlaceId].includes(modelValue.value?.placeId);
		try {
			const placePrediction = placePredictionMap.value.get(geoAddress.placeId);
			if (!placePrediction) {
				console.warn("PlacePrediction not found for place_id:", geoAddress.placeId);
				// So we can't save a value to the API missing a latitude and longitude.
				// Restore the last valid value; a failed lookup is not an explicit user clear.
				if (!modelValue.value?.latitude || !modelValue.value?.longitude) {
					modelValue.value = selectionFallbackModelValue ?? null;
					selectedModelValue = selectionFallbackModelValue ?? null;
				}
				return;
			}
			const place = placePrediction.toPlace();
			const result = await place.fetchFields({
				fields: ["id", "displayName", "formattedAddress", "location", "addressComponents"]
			});
			const placeResult = result.place;
			const parts = parseAddressComponents(placeResult.addressComponents);
			const resolvedModelValue: GeoAddress = {
				...geoAddress,
				latitude: placeResult.location?.lat() || 0,
				longitude: placeResult.location?.lng() || 0,
				formattedAddress: options.preservePredictionLabel ? geoAddress.formattedAddress : placeResult.formattedAddress || "",
				placeId: placeResult.id || geoAddress.placeId,
				streetNumber: parts.streetNumber,
				street: parts.street,
				city: parts.city,
				state: parts.state,
				postalCode: parts.postalCode,
				country: parts.country
			};
			resolvedPlaceId = resolvedModelValue.placeId;
			resolvedFormattedAddress = resolvedModelValue.formattedAddress;
			const applySelection = () => {
				const modelValueRef = modelValue.value;
				// I cant control when google returns, if they switch values multiple times, this could be old values. Make sure you check the placeIds match.
				if (isCurrentSelection() && modelValueRef) {
					Object.assign(modelValueRef, resolvedModelValue);
					options.onPlaceChanged?.(modelValueRef);
					if (applyResolvedSelection === applySelection) {
						applyResolvedSelection = null;
					}
				}
			};
			if (selectedRequestVersion === selectionRequestVersion && selectedModelValue?.placeId === geoAddress.placeId) {
				selectedModelValue = resolvedModelValue;
				applyResolvedSelection = applySelection;
			}
			applySelection();
		} catch (error) {
			console.error("Error fetching place by ID:", error);
			const failedSelectionIsCurrentModel = modelValue.value?.placeId === geoAddress.placeId;
			if (
				selectedRequestVersion === selectionRequestVersion &&
				(failedSelectionIsCurrentModel || selectedModelValue?.placeId === geoAddress.placeId)
			) {
				const shouldRestoreFallback = failedSelectionIsCurrentModel && isCurrentSelection();
				selectedModelValue = selectionFallbackModelValue ?? null;
				applyResolvedSelection = null;
				selectedPlaceFailed = failedSelectionIsCurrentModel && !shouldRestoreFallback;
				if (shouldRestoreFallback) {
					modelValue.value = selectionFallbackModelValue ?? null;
				}
			}
		}
	}

	async function selectPrediction(geoAddress: GeoAddress | null) {
		const fallbackModelValue =
			pendingSelectedChange || applyResolvedSelection || selectedPlaceFailed ? selectionFallbackModelValue : modelValue.value;
		userEditedText.value = false;
		editVersion += 1;
		selectionRequestVersion += 1;
		setPendingChange(null);
		pendingSelectedChange = null;
		applyResolvedSelection = null;
		selectedModelValue = null;
		selectedPlaceFailed = false;
		const selectionEditVersion = editVersion;
		const selectedRequestVersion = selectionRequestVersion;
		if (!geoAddress) {
			selectionFallbackModelValue = null;
			modelValue.value = null;
			options.onPlaceChanged?.(null);
			return;
		}
		modelValue.value = geoAddress;
		if (!geoAddress.placeId) {
			console.warn("Place ID is required");
			return;
		}
		if (isManualAddress(geoAddress)) {
			return;
		}
		selectionFallbackModelValue = fallbackModelValue;
		selectedModelValue = geoAddress;
		return trackPendingChange(resolveSelectedPlace(geoAddress, selectionEditVersion, selectedRequestVersion), true);
	}

	return {
		modelValue,
		items,
		isLoading,
		isGeocodingManual,
		userEditedText,
		searchTerm,
		setSearchTermSilent,
		onSearchInput,
		fetchPlacesBySearchTerm,
		handleBlur,
		commitPendingInput,
		selectPrediction,
		createManualAddress
	};
}
